import { useUnit } from 'effector-react'
import type { InfiniteData } from 'react-query'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import type { CollectionsItem } from '@/dialogs/CollectionsDialog/CollectionsList/types'
import { $creatorId } from '@/models/creators.ts'
import { listsService } from '@/modules/chatting/service/lists'
import type {
  GetListUsersResponse,
  GetListsResponse,
} from '@/modules/chatting/types'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { handleError } from '@/utils/error'

import { DialogProps } from '../types'

interface Props extends DialogProps {
  collection: CollectionsItem
}

export const ClearCollectionConfirmationDialog = ({
  isOpen,
  onClose,
  collection,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const clearListMutation = useMutation(
    () =>
      listsService.clearList({ listId: collection.id, creatorId: creatorId! }),
    {
      onSuccess: () => {
        queryClient.setQueriesData<
          InfiniteData<GetListUsersResponse> | undefined
        >(['listUsers', creatorId, collection.id], (data) => {
          if (!data) {
            return data
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              list: [],
            })),
          }
        })
        queryClient.setQueriesData<InfiniteData<GetListsResponse> | undefined>(
          'all-lists-extended',
          (oldData) => {
            if (!oldData) return oldData

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                list: page.list.map((l) =>
                  l.id === collection.id
                    ? { ...l, users: [], usersCount: 0 }
                    : l,
                ),
              })),
            }
          },
        )
        onClose()
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while clearing list')
      },
    },
  )

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[533px]"
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Clear List</Dialog.Title>
          <Dialog.Description className="text-center">
            Are you sure you want to clear {unescapeHtmlText(collection.name)}?
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button
            variant="destructive"
            isLoading={clearListMutation.isLoading}
            onClick={() => clearListMutation.mutate()}
          >
            Clear
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
